import {
  Bell,
  CalendarClock,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ListTodo,
  Mail,
  MessageCircle,
  PhoneCall,
  Search,
  X,
  type LucideIcon,
} from 'lucide-react'
import { createPortal } from 'react-dom'
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
} from 'react'
import type {
  CrmDataset,
  Lead,
} from '../../types/crm'
import {
  createReminder,
  type ReminderType,
} from './reminderClient'
import { LeadWebsiteLogo } from '../leads/LeadWebsiteLogo'
import { getLeadWebsite } from '../leads/unifiedLeadFields'
import './SetReminderModal.css'

type QuickChoice =
  | 'later-today'
  | 'tomorrow'
  | 'three-days'
  | 'next-week'
  | null

type ReminderTypeOption = {
  value: ReminderType
  label: string
  detail: string
  icon: LucideIcon
}

const REMINDER_TYPES: ReminderTypeOption[] = [
  {
    value: 'follow-up',
    label: 'Follow Up',
    detail: 'Reconnect with this prospect',
    icon: CalendarClock,
  },
  {
    value: 'call',
    label: 'Call',
    detail: 'Make a scheduled phone call',
    icon: PhoneCall,
  },
  {
    value: 'message',
    label: 'Message',
    detail: 'Send a direct message',
    icon: MessageCircle,
  },
  {
    value: 'email',
    label: 'Email',
    detail: 'Send an email follow-up',
    icon: Mail,
  },
  {
    value: 'other',
    label: 'Other',
    detail: 'Create a custom next action',
    icon: ListTodo,
  },
]

const WEEKDAYS = [
  'Su',
  'Mo',
  'Tu',
  'We',
  'Th',
  'Fr',
  'Sa',
]

function dateKey(date: Date): string {
  const year = date.getFullYear()

  const month = String(
    date.getMonth() + 1,
  ).padStart(2, '0')

  const day = String(
    date.getDate(),
  ).padStart(2, '0')

  return `${year}-${month}-${day}`
}

function dateFromKey(
  value: string,
): Date | null {
  const match =
    /^(\d{4})-(\d{2})-(\d{2})$/.exec(
      value,
    )

  if (!match) {
    return null
  }

  return new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
    12,
    0,
    0,
    0,
  )
}

function startOfDay(
  date: Date,
): Date {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  )
}

function sameDay(
  left: Date,
  right: Date,
): boolean {
  return (
    left.getFullYear() ===
      right.getFullYear() &&
    left.getMonth() ===
      right.getMonth() &&
    left.getDate() ===
      right.getDate()
  )
}

function buildMonth(
  month: Date,
): Date[] {
  const first =
    new Date(
      month.getFullYear(),
      month.getMonth(),
      1,
    )

  const start =
    new Date(first)

  start.setDate(
    first.getDate() -
      first.getDay(),
  )

  return Array.from(
    { length: 42 },
    (_, index) => {
      const date =
        new Date(start)

      date.setDate(
        start.getDate() + index,
      )

      return date
    },
  )
}

function leadOwner(
  lead: Lead,
): string {
  const owner =
    lead.opportunity?.name?.trim() ||
    lead.owner?.trim() ||
    ''

  if (
    !owner ||
    /^not identified$/i.test(owner)
  ) {
    return 'Owner not identified'
  }

  return owner
}

function leadCompany(
  lead: Lead,
): string {
  const company =
    lead.company?.trim() ||
    lead.organization?.name?.trim() ||
    ''

  return company || leadOwner(lead)
}

/** A short "City, ST" line when the lead has one. */
function leadPlace(
  lead: Lead,
): string {
  return [
    lead.organization?.city,
    lead.organization?.state,
  ]
    .map((part) => part?.trim() ?? '')
    .filter(Boolean)
    .join(', ')
}

/** Company logo (or the business-icon fallback), name, owner and place. */
function ProspectIdentity({
  lead,
}: {
  lead: Lead
}) {
  const place = leadPlace(lead)

  return (
    <>
      <LeadWebsiteLogo
        website={getLeadWebsite(lead)}
      />

      <span className="reminder-prospect-copy">
        <strong>
          {leadCompany(lead)}
        </strong>

        <small>
          {leadOwner(lead)}
          {place ? (
            <span className="reminder-prospect-place">
              {' · '}
              {place}
            </span>
          ) : null}
        </small>
      </span>
    </>
  )
}

function typeOption(
  value: ReminderType,
): ReminderTypeOption {
  return (
    REMINDER_TYPES.find(
      (option) =>
        option.value === value,
    ) ??
    REMINDER_TYPES[0]
  )
}

function composeSchedule(
  day: string,
  hour: string,
  minute: string,
  period: 'AM' | 'PM',
): Date | null {
  const selected =
    dateFromKey(day)

  if (!selected) {
    return null
  }

  const hourNumber =
    Number(hour)

  const minuteNumber =
    Number(minute)

  if (
    !Number.isInteger(hourNumber) ||
    hourNumber < 1 ||
    hourNumber > 12 ||
    !Number.isInteger(minuteNumber) ||
    minuteNumber < 0 ||
    minuteNumber > 59
  ) {
    return null
  }

  let twentyFourHour =
    hourNumber % 12

  if (period === 'PM') {
    twentyFourHour += 12
  }

  selected.setHours(
    twentyFourHour,
    minuteNumber,
    0,
    0,
  )

  return selected
}

function displaySchedule(
  date: Date,
): string {
  return date.toLocaleString(
    undefined,
    {
      dateStyle: 'medium',
      timeStyle: 'short',
    },
  )
}

export function SetReminderModal({
  dataset,
  onClose,
}: {
  dataset: CrmDataset
  onClose: () => void
}) {
  const today =
    useMemo(
      () => new Date(),
      [],
    )

  const [
    query,
    setQuery,
  ] = useState('')

  const [
    selectedLead,
    setSelectedLead,
  ] = useState<Lead | null>(null)

  const [
    reminderType,
    setReminderType,
  ] = useState<ReminderType>(
    'follow-up',
  )

  const [
    typeMenuOpen,
    setTypeMenuOpen,
  ] = useState(false)

  const [
    calendarOpen,
    setCalendarOpen,
  ] = useState(false)

  const [
    calendarMonth,
    setCalendarMonth,
  ] = useState(
    new Date(
      today.getFullYear(),
      today.getMonth(),
      1,
    ),
  )

  const [
    dateValue,
    setDateValue,
  ] = useState('')

  const [
    timeHour,
    setTimeHour,
  ] = useState('')

  const [
    timeMinute,
    setTimeMinute,
  ] = useState('')

  const [
    timePeriod,
    setTimePeriod,
  ] = useState<
    'AM' | 'PM'
  >('AM')

  const [
    quickChoice,
    setQuickChoice,
  ] = useState<QuickChoice>(null)

  const [
    note,
    setNote,
  ] = useState('')

  const [
    notify,
    setNotify,
  ] = useState(true)

  const [
    submitting,
    setSubmitting,
  ] = useState(false)

  const [
    formError,
    setFormError,
  ] = useState('')

  const [
    saved,
    setSaved,
  ] = useState(false)

  const [
    isClosing,
    setIsClosing,
  ] = useState(false)

  const searchRef =
    useRef<HTMLInputElement>(null)

  const typeMenuRef =
    useRef<HTMLDivElement>(null)

  const calendarRef =
    useRef<HTMLDivElement>(null)

  const [
    calendarClosing,
    setCalendarClosing,
  ] = useState(false)

  const calendarTimerRef =
    useRef<number | null>(null)

  // The calendar plays a short exit before it unmounts.
  const closeCalendar =
    useCallback(() => {
      if (calendarTimerRef.current !== null) {
        return
      }

      const reduceMotion =
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches

      setCalendarClosing(true)

      calendarTimerRef.current =
        window.setTimeout(() => {
          calendarTimerRef.current = null
          setCalendarClosing(false)
          setCalendarOpen(false)
        }, reduceMotion ? 0 : 150)
    }, [])

  const openCalendar =
    useCallback(() => {
      if (calendarTimerRef.current !== null) {
        window.clearTimeout(calendarTimerRef.current)
        calendarTimerRef.current = null
      }

      setCalendarClosing(false)
      setCalendarOpen(true)
    }, [])

  useEffect(
    () => () => {
      if (calendarTimerRef.current !== null) {
        window.clearTimeout(calendarTimerRef.current)
      }
    },
    [],
  )

  const closeTimerRef =
    useRef<number | null>(null)

  const closingRef =
    useRef(false)

  const results =
    useMemo(() => {
      const normalized =
        query
          .trim()
          .toLowerCase()

      return [...dataset.leads]
        .filter((lead) => {
          if (!normalized) {
            return true
          }

          return [
            leadCompany(lead),
            leadOwner(lead),
          ]
            .join(' ')
            .toLowerCase()
            .includes(normalized)
        })
        .sort((left, right) =>
          leadCompany(left)
            .localeCompare(
              leadCompany(right),
            ),
        )
    }, [
      dataset.leads,
      query,
    ])

  const activeType =
    typeOption(reminderType)

  const ActiveTypeIcon =
    activeType.icon

  const calendarDays =
    useMemo(
      () =>
        buildMonth(
          calendarMonth,
        ),
      [calendarMonth],
    )

  const scheduledDate =
    useMemo(
      () =>
        composeSchedule(
          dateValue,
          timeHour,
          timeMinute,
          timePeriod,
        ),
      [
        dateValue,
        timeHour,
        timeMinute,
        timePeriod,
      ],
    )

  const scheduleIsPast =
    Boolean(
      scheduledDate &&
      scheduledDate.getTime() <=
        Date.now(),
    )

  const scheduleReady =
    Boolean(
      scheduledDate &&
      !scheduleIsPast,
    )

  const scheduleLabel =
    scheduledDate
      ? displaySchedule(
          scheduledDate,
        )
      : dateValue
        ? `${dateFromKey(
            dateValue,
          )?.toLocaleDateString(
            undefined,
            {
              dateStyle: 'medium',
            },
          )} · Add time`
        : 'Choose date & time'

  const beginClose =
    useCallback(() => {
      if (closingRef.current) {
        return
      }

      closingRef.current = true
      setIsClosing(true)

      const reduceMotion =
        window.matchMedia?.(
          '(prefers-reduced-motion: reduce)',
        ).matches ?? false

      closeTimerRef.current =
        window.setTimeout(
          onClose,
          reduceMotion
            ? 0
            : 180,
        )
    }, [onClose])

  useEffect(() => {
    searchRef.current?.focus()

    const previousOverflow =
      document.body.style.overflow

    document.body.style.overflow =
      'hidden'

    function handleEscape(
      event: KeyboardEvent,
    ) {
      if (event.key !== 'Escape') {
        return
      }

      event.preventDefault()

      if (calendarOpen) {
        closeCalendar()
        return
      }

      if (typeMenuOpen) {
        setTypeMenuOpen(false)
        return
      }

      beginClose()
    }

    function handlePointerDown(
      event: PointerEvent,
    ) {
      const target =
        event.target as Node

      if (
        typeMenuOpen &&
        typeMenuRef.current &&
        !typeMenuRef.current.contains(
          target,
        )
      ) {
        setTypeMenuOpen(false)
      }

      if (
        calendarOpen &&
        calendarRef.current &&
        !calendarRef.current.contains(
          target,
        )
      ) {
        closeCalendar()
      }
    }

    document.addEventListener(
      'keydown',
      handleEscape,
    )

    document.addEventListener(
      'pointerdown',
      handlePointerDown,
    )

    return () => {
      document.body.style.overflow =
        previousOverflow

      document.removeEventListener(
        'keydown',
        handleEscape,
      )

      document.removeEventListener(
        'pointerdown',
        handlePointerDown,
      )

      if (
        closeTimerRef.current !== null
      ) {
        window.clearTimeout(
          closeTimerRef.current,
        )
      }
    }
  }, [
    beginClose,
    calendarOpen,
    closeCalendar,
    typeMenuOpen,
  ])

  function chooseDate(
    date: Date,
  ) {
    if (
      startOfDay(date).getTime() <
      startOfDay(new Date()).getTime()
    ) {
      return
    }

    setDateValue(
      dateKey(date),
    )

    setCalendarMonth(
      new Date(
        date.getFullYear(),
        date.getMonth(),
        1,
      ),
    )

    setTimeHour('')
    setTimeMinute('')
    setQuickChoice(null)
    setFormError('')
  }

  function chooseQuick(
    choice: Exclude<
      QuickChoice,
      null
    >,
  ) {
    const date =
      new Date()

    if (
      choice ===
      'tomorrow'
    ) {
      date.setDate(
        date.getDate() + 1,
      )
    }

    if (
      choice ===
      'three-days'
    ) {
      date.setDate(
        date.getDate() + 3,
      )
    }

    if (
      choice ===
      'next-week'
    ) {
      date.setDate(
        date.getDate() + 7,
      )
    }

    setDateValue(
      dateKey(date),
    )

    setCalendarMonth(
      new Date(
        date.getFullYear(),
        date.getMonth(),
        1,
      ),
    )

    setTimeHour('')
    setTimeMinute('')
    setQuickChoice(choice)
    openCalendar()
    setTypeMenuOpen(false)
    setFormError('')
  }

  async function submitReminder(
    event: FormEvent,
  ) {
    event.preventDefault()

    if (!selectedLead) {
      setFormError(
        'Select a prospect first.',
      )
      return
    }

    if (!scheduledDate) {
      setFormError(
        'Choose a date and enter a time.',
      )
      return
    }

    if (
      scheduledDate.getTime() <=
      Date.now()
    ) {
      setFormError(
        'The reminder must be scheduled for a future time.',
      )
      return
    }

    setSubmitting(true)
    setFormError('')

    try {
      const timezone =
        Intl.DateTimeFormat()
          .resolvedOptions()
          .timeZone ||
        'UTC'

      await createReminder({
        leadId:
          selectedLead.id,

        company:
          leadCompany(
            selectedLead,
          ),

        reminderType,

        note,

        scheduledFor:
          scheduledDate.toISOString(),

        timezone,

        notify,
      })

      setSaved(true)
    } catch (cause) {
      setFormError(
        cause instanceof Error
          ? cause.message
          : 'The reminder could not be saved.',
      )
    } finally {
      setSubmitting(false)
    }
  }

  const selectedCompany =
    selectedLead
      ? leadCompany(
          selectedLead,
        )
      : ''

  const selectedDate =
    dateFromKey(dateValue)

  const currentMonthStart =
    new Date(
      new Date().getFullYear(),
      new Date().getMonth(),
      1,
    )

  const canGoBack =
    calendarMonth.getTime() >
    currentMonthStart.getTime()

  return createPortal(
    <div
      className={`lead-modal-layer${
        isClosing
          ? ' is-closing'
          : ''
      }`}
      data-testid="set-reminder-modal"
    >
      <button
        aria-label="Close Set Reminder"
        className="lead-modal-backdrop"
        onClick={beginClose}
        type="button"
      />

      <section
        aria-labelledby="set-reminder-title"
        aria-modal="true"
        className="lead-modal reminder-modal"
        role="dialog"
      >
        <header className="reminder-modal-header">
          <div className="reminder-modal-heading">
            <span>
              <Bell
                aria-hidden="true"
                size={15}
              />
              Prospect reminder
            </span>

            <h2 id="set-reminder-title">
              Set Reminder
            </h2>

            <p>
              Schedule the next action for a specific prospect.
            </p>
          </div>

          <button
            aria-label="Close Set Reminder"
            className="reminder-close"
            onClick={beginClose}
            type="button"
          >
            <X
              aria-hidden="true"
              size={18}
              strokeWidth={2}
            />
          </button>
        </header>

        {saved ? (
          <div className="reminder-success">
            <span className="reminder-success-icon">
              <CheckCircle2
                aria-hidden="true"
                size={30}
              />
            </span>

            <h3>
              Reminder scheduled
            </h3>

            <p>
              William's Treasure saved this reminder for{' '}
              <strong>
                {selectedCompany}
              </strong>.
            </p>

            {scheduledDate ? (
              <div className="reminder-success-time">
                <CalendarDays
                  aria-hidden="true"
                  size={17}
                />

                <span>
                  {displaySchedule(
                    scheduledDate,
                  )}
                </span>
              </div>
            ) : null}

            <button
              className="button button-primary"
              onClick={beginClose}
              type="button"
            >
              Done
            </button>
          </div>
        ) : (
          <form
            className="reminder-form"
            onSubmit={submitReminder}
          >
            <section className="reminder-section">
              <div className="reminder-field-heading">
                <label
                  className="reminder-label"
                  htmlFor="reminder-prospect-search"
                >
                  Prospect
                </label>

                {!selectedLead ? (
                  <small className="reminder-count">
                    {results.length}{' '}
                    {results.length === 1
                      ? 'prospect'
                      : 'prospects'}
                  </small>
                ) : null}
              </div>

              <div className="reminder-search">
                <Search
                  aria-hidden="true"
                  size={17}
                />

                <input
                  autoComplete="off"
                  id="reminder-prospect-search"
                  onChange={(event) => {
                    setQuery(
                      event.target.value,
                    )

                    if (
                      selectedLead
                    ) {
                      setSelectedLead(
                        null,
                      )
                    }
                  }}
                  placeholder="Search company or owner..."
                  ref={searchRef}
                  type="search"
                  value={query}
                />
              </div>

              {selectedLead ? (
                <button
                  className="reminder-selected-prospect"
                  onClick={() => {
                    setSelectedLead(
                      null,
                    )

                    setQuery('')

                    requestAnimationFrame(
                      () =>
                        searchRef.current?.focus(),
                    )
                  }}
                  type="button"
                >
                  <ProspectIdentity
                    lead={selectedLead}
                  />

                  <em>
                    Change
                  </em>
                </button>
              ) : (
                <div
                  className="reminder-results"
                  role="list"
                >
                  {results.length > 0 ? (
                    results.map(
                      (lead) => (
                        <button
                          key={lead.id}
                          onClick={() => {
                            setSelectedLead(
                              lead,
                            )

                            setQuery(
                              leadCompany(
                                lead,
                              ),
                            )

                            setFormError(
                              '',
                            )
                          }}
                          role="listitem"
                          type="button"
                        >
                          <ProspectIdentity
                            lead={lead}
                          />
                        </button>
                      ),
                    )
                  ) : (
                    <p>
                      No matching prospect found.
                    </p>
                  )}
                </div>
              )}
            </section>

            <section className="reminder-section">
              <span className="reminder-label">
                Reminder
              </span>

              <div
                className="reminder-type-select"
                ref={typeMenuRef}
              >
                <button
                  aria-expanded={typeMenuOpen}
                  aria-haspopup="menu"
                  className={`reminder-type-trigger${
                    typeMenuOpen
                      ? ' is-open'
                      : ''
                  }`}
                  onClick={() => {
                    setTypeMenuOpen(
                      (current) =>
                        !current,
                    )

                    if (calendarOpen) {
                      closeCalendar()
                    }
                  }}
                  type="button"
                >
                  <span className="reminder-type-icon">
                    <ActiveTypeIcon
                      aria-hidden="true"
                      size={17}
                    />
                  </span>

                  <strong>
                    {activeType.label}
                  </strong>

                  <ChevronDown
                    aria-hidden="true"
                    className="reminder-chevron"
                    size={17}
                  />
                </button>

                {typeMenuOpen ? (
                  <div
                    className="reminder-type-menu"
                    role="menu"
                  >
                    {REMINDER_TYPES.map(
                      (option) => {
                        const Icon =
                          option.icon

                        const selected =
                          option.value ===
                          reminderType

                        return (
                          <button
                            className={
                              selected
                                ? 'is-selected'
                                : ''
                            }
                            key={
                              option.value
                            }
                            onClick={() => {
                              setReminderType(
                                option.value,
                              )

                              setTypeMenuOpen(
                                false,
                              )
                            }}
                            role="menuitem"
                            type="button"
                          >
                            <span className="reminder-type-icon">
                              <Icon
                                aria-hidden="true"
                                size={17}
                              />
                            </span>

                            <span>
                              <strong>
                                {option.label}
                              </strong>

                              <small>
                                {option.detail}
                              </small>
                            </span>
                          </button>
                        )
                      },
                    )}
                  </div>
                ) : null}
              </div>
            </section>

            <section className="reminder-section">
              <span className="reminder-label">
                When
              </span>

              <div className="reminder-quick-times">
                <button
                  className={
                    quickChoice ===
                    'later-today'
                      ? 'is-active'
                      : ''
                  }
                  onClick={() =>
                    chooseQuick(
                      'later-today',
                    )
                  }
                  type="button"
                >
                  Later Today
                </button>

                <button
                  className={
                    quickChoice ===
                    'tomorrow'
                      ? 'is-active'
                      : ''
                  }
                  onClick={() =>
                    chooseQuick(
                      'tomorrow',
                    )
                  }
                  type="button"
                >
                  Tomorrow
                </button>

                <button
                  className={
                    quickChoice ===
                    'three-days'
                      ? 'is-active'
                      : ''
                  }
                  onClick={() =>
                    chooseQuick(
                      'three-days',
                    )
                  }
                  type="button"
                >
                  3 Days
                </button>

                <button
                  className={
                    quickChoice ===
                    'next-week'
                      ? 'is-active'
                      : ''
                  }
                  onClick={() =>
                    chooseQuick(
                      'next-week',
                    )
                  }
                  type="button"
                >
                  Next Week
                </button>
              </div>

              <div
                className="reminder-calendar-control"
                ref={calendarRef}
              >
                <button
                  aria-expanded={calendarOpen}
                  className={`reminder-calendar-trigger${
                    calendarOpen
                      ? ' is-open'
                      : ''
                  }`}
                  onClick={() => {
                    if (calendarOpen) {
                      closeCalendar()
                    } else {
                      openCalendar()
                    }

                    setTypeMenuOpen(false)
                  }}
                  type="button"
                >
                  <CalendarClock
                    aria-hidden="true"
                    size={18}
                  />

                  <span>
                    <strong>
                      {scheduleLabel}
                    </strong>

                    <small>
                      Date and time required
                    </small>
                  </span>

                  <ChevronDown
                    aria-hidden="true"
                    className="reminder-chevron"
                    size={17}
                  />
                </button>

                {calendarOpen ? (
                  <div
                    aria-label="Choose reminder date and time"
                    className={`reminder-calendar${
                      calendarClosing
                        ? ' is-closing'
                        : ''
                    }`}
                    role="dialog"
                  >
                    <div className="reminder-calendar-month">
                      <header className="reminder-calendar-header">
                        <button
                          aria-label="Previous month"
                          disabled={!canGoBack}
                          onClick={() =>
                            setCalendarMonth(
                              new Date(
                                calendarMonth.getFullYear(),
                                calendarMonth.getMonth() - 1,
                                1,
                              ),
                            )
                          }
                          type="button"
                        >
                          <ChevronLeft
                            aria-hidden="true"
                            size={18}
                          />
                        </button>

                        <strong aria-live="polite">
                          {calendarMonth.toLocaleDateString(
                            undefined,
                            {
                              month: 'long',
                              year: 'numeric',
                            },
                          )}
                        </strong>

                        <button
                          aria-label="Next month"
                          onClick={() =>
                            setCalendarMonth(
                              new Date(
                                calendarMonth.getFullYear(),
                                calendarMonth.getMonth() + 1,
                                1,
                              ),
                            )
                          }
                          type="button"
                        >
                          <ChevronRight
                            aria-hidden="true"
                            size={18}
                          />
                        </button>
                      </header>

                      <div className="reminder-calendar-weekdays">
                        {WEEKDAYS.map(
                          (weekday) => (
                            <span
                              aria-hidden="true"
                              key={weekday}
                            >
                              {weekday}
                            </span>
                          ),
                        )}
                      </div>

                      <div className="reminder-calendar-grid">
                        {calendarDays.map(
                          (day) => {
                            const past =
                              startOfDay(
                                day,
                              ).getTime() <
                              startOfDay(
                                new Date(),
                              ).getTime()

                            const outside =
                              day.getMonth() !==
                              calendarMonth.getMonth()

                            const selected =
                              Boolean(
                                selectedDate &&
                                sameDay(
                                  day,
                                  selectedDate,
                                ),
                              )

                            const isToday =
                              sameDay(
                                day,
                                new Date(),
                              )

                            return (
                              <button
                                aria-current={
                                  isToday
                                    ? 'date'
                                    : undefined
                                }
                                aria-pressed={selected}
                                aria-label={day.toLocaleDateString(
                                  undefined,
                                  {
                                    dateStyle:
                                      'full',
                                  },
                                )}
                                className={[
                                  outside
                                    ? 'is-outside'
                                    : '',
                                  selected
                                    ? 'is-selected'
                                    : '',
                                  isToday
                                    ? 'is-today'
                                    : '',
                                ]
                                  .filter(Boolean)
                                  .join(' ')}
                                disabled={past}
                                key={
                                  dateKey(day)
                                }
                                onClick={() =>
                                  chooseDate(
                                    day,
                                  )
                                }
                                type="button"
                              >
                                {day.getDate()}
                              </button>
                            )
                          },
                        )}
                      </div>
                    </div>

                    <div className="reminder-calendar-side">
                      {dateValue ? (
                        <div className="reminder-calendar-time">
                          <div className="reminder-time-heading">
                            <span>
                              Time
                            </span>

                            <small>
                              Required
                            </small>
                          </div>

                          <div className="reminder-time-controls">
                            <input
                              aria-label="Hour"
                              inputMode="numeric"
                              maxLength={2}
                              onChange={(event) =>
                                setTimeHour(
                                  event.target.value
                                    .replace(
                                      /\D/g,
                                      '',
                                    )
                                    .slice(
                                      0,
                                      2,
                                    ),
                                )
                              }
                              placeholder="10"
                              value={timeHour}
                            />

                            <span>
                              :
                            </span>

                            <input
                              aria-label="Minute"
                              inputMode="numeric"
                              maxLength={2}
                              onBlur={() => {
                                if (
                                  timeMinute.length ===
                                  1
                                ) {
                                  setTimeMinute(
                                    `0${timeMinute}`,
                                  )
                                }
                              }}
                              onChange={(event) =>
                                setTimeMinute(
                                  event.target.value
                                    .replace(
                                      /\D/g,
                                      '',
                                    )
                                    .slice(
                                      0,
                                      2,
                                    ),
                                )
                              }
                              placeholder="00"
                              value={timeMinute}
                            />

                            <div className="reminder-period">
                              <button
                                className={
                                  timePeriod ===
                                  'AM'
                                    ? 'is-active'
                                    : ''
                                }
                                onClick={() =>
                                  setTimePeriod(
                                    'AM',
                                  )
                                }
                                type="button"
                              >
                                AM
                              </button>

                              <button
                                className={
                                  timePeriod ===
                                  'PM'
                                    ? 'is-active'
                                    : ''
                                }
                                onClick={() =>
                                  setTimePeriod(
                                    'PM',
                                  )
                                }
                                type="button"
                              >
                                PM
                              </button>
                            </div>
                          </div>

                          {scheduleIsPast ? (
                            <small className="reminder-calendar-error">
                              Choose a future time.
                            </small>
                          ) : null}

                          <button
                            className="reminder-calendar-confirm"
                            disabled={
                              !scheduleReady
                            }
                            onClick={() => {
                              if (
                                scheduleReady
                              ) {
                                closeCalendar()
                              }
                            }}
                            type="button"
                          >
                            Use this date & time
                          </button>
                        </div>
                      ) : (
                        <p className="reminder-calendar-help">
                          Select a day to continue with the required time.
                        </p>
                      )}
                    </div>
                  </div>
                ) : null}
              </div>
            </section>

            <section className="reminder-section">
              <label
                className="reminder-label"
                htmlFor="reminder-note"
              >
                Note

                <small>
                  Optional
                </small>
              </label>

              <textarea
                id="reminder-note"
                maxLength={2000}
                onChange={(event) =>
                  setNote(
                    event.target.value,
                  )
                }
                placeholder="Example: Ask if he reviewed the website proposal."
                rows={3}
                value={note}
              />
            </section>

            <label className="reminder-notify">
              <div>
                <Bell
                  aria-hidden="true"
                  size={18}
                />

                <span>
                  <strong>
                    Notify me
                  </strong>

                  <small>
                    Save this reminder for device notifications.
                  </small>
                </span>
              </div>

              <input
                checked={notify}
                onChange={(event) =>
                  setNotify(
                    event.target.checked,
                  )
                }
                type="checkbox"
              />

              <span
                aria-hidden="true"
                className="reminder-switch"
              />
            </label>

            {formError ? (
              <p
                className="review-form-error"
                role="alert"
              >
                {formError}
              </p>
            ) : null}

            <div className="reminder-actions">
              <button
                className="button button-secondary"
                disabled={submitting}
                onClick={beginClose}
                type="button"
              >
                Cancel
              </button>

              <button
                className="button button-primary"
                disabled={
                  submitting ||
                  !selectedLead ||
                  !scheduleReady
                }
                type="submit"
              >
                {submitting
                  ? 'Saving Reminder...'
                  : 'Set Reminder'}
              </button>
            </div>
          </form>
        )}
      </section>
    </div>,
    document.body,
  )
}